import styled, { css } from 'styled-components';

import { COLORS, MOBILE } from '../../theme/tokens';
import { hoverWash } from '../../theme/mixins';

export type ButtonVariant = 'primary' | 'outline' | 'text';

const VARIANT = {
  primary: css`
    background: ${COLORS.ctaSolid};
    border: 1px solid ${COLORS.ctaSolid};
    color: ${COLORS.ctaSolidText};
    font-weight: 600;
  `,
  outline: css`
    /* Solid (opaque) surface, not transparent, so content behind the button never shows
       through while scrolling. Theme-aware via the token (dark surface / light inset). */
    background: ${COLORS.ctaOutlineBg};
    border: 1px solid ${COLORS.ctaBorder};
    color: ${COLORS.ctaText};
  `,
  // Borderless ghost — a plain tappable text+icon row (e.g. "Load more").
  text: css`
    background: transparent;
    border: 1px solid transparent;
    color: ${COLORS.ctaText};
  `,
};

/**
 * Icon-only: a square tap target instead of a pill with copy. 40px on desktop
 * where a mouse points precisely, widened back to the app's 48px control size on
 * mobile so it clears the minimum comfortable thumb target.
 */
const ICON_ONLY = css`
  width: 40px;
  height: 40px;
  padding: 0;
  gap: 0;

  ${MOBILE} {
    width: 48px;
    height: 48px;
  }
`;

export const Root = styled.button<{ $variant: ButtonVariant; $fullWidth: boolean; $iconOnly: boolean }>`
  display: ${(p) => (p.$fullWidth ? 'flex' : 'inline-flex')};
  width: ${(p) => (p.$fullWidth ? '100%' : 'auto')};
  /* Fixed 48px is the app-wide control height — every Button is the same height so
     primary + secondary (outline) CTAs line up wherever they sit together. */
  box-sizing: border-box;
  height: 48px;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 0 18px;
  border-radius: 99px;
  font-size: 16px;
  cursor: pointer;
  ${(p) => VARIANT[p.$variant]}
  ${(p) => (p.$iconOnly ? ICON_ONLY : null)}

  /* Cursor-following hover glow + lit hairline ring, shared with the clickable
     cards (driven by the single usePointerGlow controller in the layout). Gives
     every CTA the same hover affordance; fades in on hover / keyboard focus. */
  ${hoverWash}

  &:disabled {
    cursor: default;
    opacity: 0.45;
  }

  /* A disabled CTA is inert — suppress the glow even though :hover still fires
     (higher specificity than hoverWash's :hover/:focus-visible rules, so it wins). */
  &:disabled:hover::before,
  &:disabled:hover::after,
  &:disabled:focus-visible::before,
  &:disabled:focus-visible::after {
    opacity: 0;
  }
`;
